import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Form, Input, Switch, message } from "antd";
import KadavraLogo from "../components/common/KadavraLogo";
import { login as loginService } from "../services/authService";
import { useAuth } from "../context/AuthContext";

function onChange(checked) {
  localStorage.setItem("remember_me", checked);
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f4f8f8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
  },
  logoWrap: {
    marginBottom: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },
  logoImg: {
    height: "40px",
    objectFit: "contain",
  },
  badge: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#44b07f",
    backgroundColor: "#e6f5ee",
    padding: "3px 10px",
    borderRadius: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)",
    padding: "36px 40px 32px",
    width: "100%",
    maxWidth: "420px",
  },
  heading: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#1b2d21",
    margin: "0 0 4px 0",
    lineHeight: 1.3,
  },
  subheading: {
    fontSize: "14px",
    color: "#6a6e76",
    margin: "0 0 28px 0",
  },
  label: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#3e4c5f",
  },
  rememberRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
  },
  rememberText: {
    fontSize: "13px",
    color: "#3e4c5f",
  },
  submitBtn: {
    width: "100%",
    height: "44px",
    backgroundColor: "#44b07f",
    borderColor: "#44b07f",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#ffffff",
    boxShadow: "none",
  },
  forgotWrap: {
    textAlign: "center",
    marginTop: "16px",
  },
  forgotLink: {
    fontSize: "13px",
    color: "#44b07f",
    fontWeight: 500,
    textDecoration: "none",
  },
  footer: {
    marginTop: "40px",
    fontSize: "12px",
    color: "#a0aab4",
    textAlign: "center",
  },
};

function SignIn() {
  const { user, login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const { email, password } = values;
      const response = await loginService({ email, password });

      if (response.profile.status === "rejected") {
        message.error(
          "Your account registration has been rejected. Please contact support for more information."
        );
        return;
      }

      authLogin(response.profile);
      setTimeout(() => {
        message.success("Login successful!");
        navigate("/dashboard");
      }, 100);
    } catch (error) {
      if (error.response?.status === 401) {
        message.error("Invalid email or password");
      } else if (error.response?.status === 403) {
        const errorCode = error.response?.data?.code;
        const errorMessage = error.response?.data?.message;

        if (errorCode === "INVALID_REQUEST" && errorMessage?.includes("reset your password")) {
          message.info(
            "Password reset required. You will be redirected to the password reset page. " +
            "Please follow the instructions to reset your password."
          );
          setTimeout(() => {
            navigate("/forgot-password");
          }, 3000);
        } else {
          message.error(
            "Your account has been disabled. Please contact support at support@getodysy.com or +234 913 278 4774"
          );
        }
      } else {
        const msg = error.response?.data?.message || error.message || "Login failed";
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.logoWrap}>
        <KadavraLogo />
        <span style={styles.badge}>Admin Portal</span>
      </div>

      <div style={styles.card}>
        <h1 style={styles.heading}>Sign in to your account</h1>
        <p style={styles.subheading}>Enter your email and password to continue</p>

        <Form onFinish={onFinish} layout="vertical">
          <Form.Item
            label={<span style={styles.label}>Email</span>}
            name="email"
            rules={[
              { required: true, message: "Please input your email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input
              placeholder="you@kadavrabdc.com"
              size="large"
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <Form.Item
            label={<span style={styles.label}>Password</span>}
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password
              placeholder="Enter your password"
              size="large"
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <div style={styles.rememberRow}>
            <Switch defaultChecked onChange={onChange} />
            <span style={styles.rememberText}>Remember me</span>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={styles.submitBtn}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div style={styles.forgotWrap}>
          <Link to="/forgot-password" style={styles.forgotLink}>
            Forgot password?
          </Link>
        </div>
      </div>

      <p style={styles.footer}>© {new Date().getFullYear()} Kadavra BDC. All rights reserved.</p>
    </div>
  );
}

export default SignIn;
